import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Eye, MousePointerClick, Percent, PlayCircle, ChevronDown, Info, Share2, Check } from "lucide-react";
import { toast } from "sonner";
import SiteShell from "../components/site/SiteShell";
import useReveal from "../lib/useReveal";
import useSEO from "../lib/useSEO";
import { overview, channels, campaigns, tactics, markets, highlights } from "../lib/demoReportData";

function useCountUp(target, duration = 1400, decimals = 0) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    let raf;
    const t0 = performance.now();
    const tick = (t) => {
      const p = Math.min((t - t0) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(target * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return decimals ? val.toFixed(decimals) : Math.round(val).toLocaleString();
}

function ShareDemoButton() {
  const [copied, setCopied] = useState(false);
  const share = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ title: "Rae Enterprise — Interactive Campaign Demo", text: "See what geofencing campaign reporting looks like.", url });
        return;
      } catch (e) {
        if (e.name === "AbortError") return;
      }
    }
    await navigator.clipboard.writeText(url);
    setCopied(true);
    toast.success("Demo link copied — send it to your decision-makers.");
    setTimeout(() => setCopied(false), 2500);
  };
  return (
    <button type="button" onClick={share} className="btn-outline-gold btn-label mt-10 inline-flex" data-testid="share-demo-btn">
      {copied ? <Check size={15} strokeWidth={1.6} /> : <Share2 size={15} strokeWidth={1.6} />}
      {copied ? "Link Copied" : "Share This Demo"}
    </button>
  );
}

function DemoBadge() {
  return (
    <span className="inline-flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-black bg-[#d4af37] px-3 py-1.5" data-testid="demo-data-badge">
      <Info size={11} strokeWidth={2} /> Anonymized Campaign Report
    </span>
  );
}

function MetricCard({ icon, label, value, suffix, sub, testId }) {
  return (
    <div
      className="bg-[#0b0b0b] p-10 lg:p-12 group hover:bg-[#111111] transition-colors duration-500 cursor-default"
      data-testid={testId}
    >
      <div className="text-[#d4af37] mb-6 group-hover:scale-110 transition-transform duration-300 origin-left">{icon}</div>
      <div className="h-display text-white text-[42px] lg:text-[52px] leading-none group-hover:text-[#d4af37] transition-colors duration-300">
        {value}{suffix}
      </div>
      <div className="text-[11px] tracking-[0.25em] uppercase text-white/50 mt-3">{label}</div>
      {sub && <div className="text-[12px] text-white/40 mt-2">{sub}</div>}
    </div>
  );
}

function CampaignRow({ c, maxImpressions, isOpen, onToggle }) {
  const share = ((c.impressions / overview.impressions) * 100).toFixed(1);
  return (
    <div className="bg-[#0b0b0b]">
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-left p-6 lg:px-8 hover:bg-[#101010] transition-colors group"
        data-testid={`demo-campaign-row-${c.id}`}
      >
        <div className="flex items-center gap-4">
          <span className="text-[11px] tracking-[0.2em] text-white/35 w-8">{String(c.id).padStart(2, "0")}</span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-4 mb-2.5">
              <span className="text-white text-[14px] tracking-[0.1em] uppercase">Ad Set {c.id}</span>
              <span className="hidden sm:block text-[11px] tracking-[0.15em] uppercase text-[#d4af37]/80">{c.tactic} · {c.format}</span>
              <span className="text-white/70 text-[13px] tabular-nums w-24 text-right">{c.impressions.toLocaleString()}</span>
              <span className={`text-[13px] tabular-nums w-14 text-right ${c.ctr >= 0.3 ? "text-[#d4af37]" : "text-white/60"}`}>{c.ctr.toFixed(2)}%</span>
              <ChevronDown size={15} strokeWidth={1.5} className={`text-[#d4af37] transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
            </div>
            <div className="h-[3px] bg-white/5 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-[#d4af37] to-[#f2c66d] transition-all duration-700"
                style={{ width: `${Math.max((c.impressions / maxImpressions) * 100, 1.5)}%` }}
              />
            </div>
          </div>
        </div>
      </button>
      {isOpen && (
        <div className="px-6 lg:px-8 pb-7 pt-1 grid grid-cols-2 sm:grid-cols-4 gap-6" data-testid={`demo-campaign-detail-${c.id}`}>
          <Detail label="Tactic" value={c.tactic} />
          <Detail label="Format" value={c.format} />
          <Detail label="Clicks" value={c.clicks.toLocaleString()} />
          <Detail label="Share Of Impressions" value={`${share}%`} />
        </div>
      )}
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div className="border-l border-[#d4af37]/25 pl-4">
      <div className="text-[10px] tracking-[0.2em] uppercase text-white/40 mb-1">{label}</div>
      <div className="text-white text-[14px]">{value}</div>
    </div>
  );
}

export default function CampaignDemoPage() {
  useReveal();
  useSEO({
    title: "Interactive Campaign Demo — Live Reporting Example",
    description: "Explore an anonymized geofencing campaign report: 1M+ impressions across 15 campaigns, channel performance, tactic breakdowns and market delivery — the reporting every Rae Enterprise client receives.",
    path: "/campaign-demo",
  });
  const [filter, setFilter] = useState("All");
  const [openRow, setOpenRow] = useState(null);

  const imp = useCountUp(overview.impressions);
  const clicks = useCountUp(overview.clicks);
  const ctr = useCountUp(overview.ctr, 1400, 2);
  const vcr = useCountUp(overview.videoCompletion, 1400, 2);

  const filtered = filter === "All" ? campaigns : campaigns.filter((c) => c.tactic === filter);
  const maxImpressions = campaigns[0].impressions;
  const maxMarket = markets[0].impressions;

  return (
    <SiteShell>
      <section className="pt-32 pb-16 border-b border-white/5" data-testid="demo-hero">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <div className="flex flex-wrap items-center gap-4 mb-6">
            <div className="kicker">Campaign Performance Demo</div>
            <DemoBadge />
          </div>
          <h1 className="h-display text-white text-[44px] sm:text-[64px] lg:text-[84px] leading-[0.9]">
            THIS IS WHAT<br /><span className="text-[#d4af37]">REPORTING LOOKS LIKE.</span>
          </h1>
          <p className="mt-8 max-w-[620px] text-white/70 text-[15px] leading-relaxed">
            A live, interactive example of the reporting Rae Enterprise clients can
            receive. Figures are anonymized, and the report has been visually
            enhanced for demonstration purposes.
          </p>
          <ShareDemoButton />
        </div>
      </section>

      {/* Overview metrics */}
      <section className="py-20" data-testid="demo-overview">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#d4af37]/10">
            <MetricCard icon={<Eye size={26} strokeWidth={1.2} />} label="Total Impressions" value={imp} testId="demo-metric-impressions" />
            <MetricCard icon={<MousePointerClick size={26} strokeWidth={1.2} />} label="Total Clicks" value={clicks} testId="demo-metric-clicks" />
            <MetricCard icon={<Percent size={26} strokeWidth={1.2} />} label="Overall CTR" value={ctr} suffix="%" testId="demo-metric-ctr" />
            <MetricCard icon={<PlayCircle size={26} strokeWidth={1.2} />} label="Video Completion" value={vcr} suffix="%" sub={`${overview.completedViews.toLocaleString()} completed views`} testId="demo-metric-vcr" />
          </div>
        </div>
      </section>

      {/* Channel summary */}
      <section className="py-20 bg-[#0d0d0d] border-y border-[#d4af37]/10" data-testid="demo-channels">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-12">
            <h2 className="h-display text-white text-[32px] sm:text-[44px]">
              CHANNEL <span className="text-[#d4af37]">SUMMARY.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#d4af37]/10">
            {channels.map((ch) => (
              <div key={ch.channel} className="bg-[#0b0b0b] p-10 hover:bg-[#111111] transition-colors duration-500" data-testid={`demo-channel-${ch.channel.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="text-[12px] tracking-[0.28em] uppercase text-[#d4af37] mb-6">{ch.channel}</div>
                <div className="flex flex-wrap gap-x-12 gap-y-6">
                  <div>
                    <div className="h-display text-white text-[34px] leading-none">{ch.impressions.toLocaleString()}</div>
                    <div className="text-[10px] tracking-[0.2em] uppercase text-white/45 mt-2">Impressions</div>
                  </div>
                  <div>
                    <div className="h-display text-white text-[34px] leading-none">{ch.clicks.toLocaleString()}</div>
                    <div className="text-[10px] tracking-[0.2em] uppercase text-white/45 mt-2">Clicks</div>
                  </div>
                  <div>
                    <div className="h-display text-[#d4af37] text-[34px] leading-none">{ch.ctr.toFixed(2)}%</div>
                    <div className="text-[10px] tracking-[0.2em] uppercase text-white/45 mt-2">CTR</div>
                  </div>
                  {ch.completionRate && (
                    <div>
                      <div className="h-display text-[#d4af37] text-[34px] leading-none">{ch.completionRate}%</div>
                      <div className="text-[10px] tracking-[0.2em] uppercase text-white/45 mt-2">Completion Rate</div>
                    </div>
                  )}
                </div>
                <div className="mt-8 h-[5px] bg-white/5">
                  <div className="h-full bg-gradient-to-r from-[#d4af37] to-[#f2c66d]" style={{ width: `${(ch.impressions / overview.impressions) * 100}%` }} />
                </div>
                <div className="text-[11px] text-white/40 mt-2">{((ch.impressions / overview.impressions) * 100).toFixed(1)}% of total delivery</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Campaign breakdown */}
      <section className="py-20" data-testid="demo-campaigns">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
            <h2 className="h-display text-white text-[32px] sm:text-[44px]">
              CAMPAIGN <span className="text-[#d4af37]">BREAKDOWN.</span>
            </h2>
          </div>
          <p className="text-white/50 text-[13px] mb-8 max-w-[560px]">
            One campaign using fifteen coordinated ad sets, and different tactics.
            Click any row to expand its tactic, format and share of delivery.
          </p>
          <div className="flex flex-wrap gap-2 mb-8" data-testid="demo-tactic-filters">
            {tactics.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => { setFilter(t); setOpenRow(null); }}
                className={`text-[11px] tracking-[0.18em] uppercase px-4 py-2 border transition-colors duration-300 ${
                  filter === t ? "border-[#d4af37] bg-[#d4af37] text-black" : "border-white/15 text-white/60 hover:border-[#d4af37]/60 hover:text-[#d4af37]"
                }`}
                data-testid={`demo-filter-${t.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {t}
              </button>
            ))}
          </div>
          <div className="hidden sm:flex items-center gap-4 px-6 lg:px-8 pb-3 text-[10px] tracking-[0.2em] uppercase text-white/35">
            <span className="w-8">#</span>
            <span className="flex-1">Campaign · Tactic</span>
            <span className="w-24 text-right">Impressions</span>
            <span className="w-14 text-right">CTR</span>
            <span className="w-[15px]" />
          </div>
          <div className="space-y-px bg-[#d4af37]/10" data-testid="demo-campaign-list">
            {filtered.map((c) => (
              <CampaignRow
                key={c.id}
                c={c}
                maxImpressions={maxImpressions}
                isOpen={openRow === c.id}
                onToggle={() => setOpenRow(openRow === c.id ? null : c.id)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Geographic breakdown */}
      <section className="py-20 bg-[#0d0d0d] border-y border-[#d4af37]/10" data-testid="demo-geo">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
            <h2 className="h-display text-white text-[32px] sm:text-[44px]">
              MARKET <span className="text-[#d4af37]">DELIVERY.</span>
            </h2>
          </div>
          <p className="text-white/50 text-[13px] mb-12 max-w-[560px]">
            Specific cities, venues and geofence names replaced with broad market labels.
            Smaller markets often punch far above their weight on CTR.
          </p>
          <div className="space-y-5">
            {markets.map((m) => (
              <div key={m.market} className="group" data-testid={`demo-market-${m.market.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="flex items-baseline justify-between gap-4 mb-2">
                  <span className="text-white text-[13px] tracking-[0.12em] uppercase">{m.market}</span>
                  <span className="text-white/50 text-[12px] tabular-nums">
                    {m.impressions.toLocaleString()} imp · <span className={m.ctr >= 0.5 ? "text-[#d4af37]" : ""}>{m.ctr.toFixed(2)}% CTR</span>
                  </span>
                </div>
                <div className="h-[6px] bg-white/5 overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-[#d4af37]/70 to-[#d4af37] group-hover:to-[#f2c66d] transition-all duration-500"
                    style={{ width: `${Math.max((m.impressions / maxMarket) * 100, 0.8)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Address Hotspot & Conversion Zone */}
      <section className="py-20" data-testid="demo-maps">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <h2 className="h-display text-white text-[32px] sm:text-[44px] mb-4">
            ADDRESS HOTSPOT &amp; <span className="text-[#d4af37]">CONVERSION ZONE.</span>
          </h2>
          <p className="text-white/50 text-[13px] mb-12 max-w-[560px]">
            Where matched households cluster — and who physically arrived after seeing
            the ads. All locations anonymized.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <MapPanel title="Address Hotspot Map" testId="demo-map-hotspot">
              <HotspotMap />
              <Legend items={[["Matched household addresses", "dot"], ["Density cluster", "glow"]]} />
            </MapPanel>
            <MapPanel title="Conversion Zone" testId="demo-map-conversion">
              <ConversionMap />
              <Legend items={[["Targeted destination", "marker"], ["Conversion boundary", "dash"], ["Attributed visits", "dot"]]} />
            </MapPanel>
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="py-20" data-testid="demo-highlights">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <h2 className="h-display text-white text-[32px] sm:text-[44px] mb-12">
            DEMO <span className="text-[#d4af37]">HIGHLIGHTS.</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#d4af37]/10">
            {highlights.map((h, i) => (
              <div key={h.title} className="bg-[#0b0b0b] p-10 hover:bg-[#111111] transition-colors duration-500" data-testid={`demo-highlight-${i}`}>
                <div className="h-display text-[#d4af37] text-[40px] leading-none mb-5">0{i + 1}</div>
                <h3 className="text-white text-[15px] tracking-[0.18em] uppercase mb-3">{h.title}</h3>
                <p className="text-white/60 text-[13px] leading-relaxed">{h.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-[#111111] border-t border-[#d4af37]/15" data-testid="demo-cta">
        <div className="max-w-[900px] mx-auto px-6 md:px-10 text-center">
          <div className="kicker mb-6">Want Numbers Like These?</div>
          <h3 className="h-display text-white text-[32px] sm:text-[48px]">
            YOUR REPORT COULD BE<br /><span className="text-[#d4af37]">NEXT.</span>
          </h3>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-5">
            <Link to="/campaign-builder" className="btn-gold btn-label" data-testid="demo-builder-cta">
              Build Your Campaign <ArrowRight size={16} strokeWidth={1.6} />
            </Link>
            <Link to="/contact" className="btn-outline-gold btn-label" data-testid="demo-contact-cta">
              Talk To Our Team
            </Link>
          </div>
        </div>
      </section>
      <section className="pb-10 pt-4" data-testid="demo-disclaimer-section">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <p className="text-white/40 text-[12px] leading-relaxed" data-testid="demo-bottom-disclaimer">
            This anonymized report is provided for illustrative purposes only. Rae
            Enterprise does not promise or guarantee the same or similar results.
            Actual campaign performance will vary.
          </p>
        </div>
      </section>
    </SiteShell>
  );
}

const rand = (i, k) => {
  const v = Math.sin(i * 127.1 + k * 311.7) * 43758.5453;
  return v - Math.floor(v);
};

const CLUSTERS = [
  { cx: 24, cy: 26, n: 16, r: 9 },
  { cx: 62, cy: 20, n: 10, r: 7 },
  { cx: 46, cy: 52, n: 18, r: 10 },
  { cx: 79, cy: 60, n: 9, r: 6 },
  { cx: 18, cy: 64, n: 7, r: 5 },
];

function MapGrid() {
  return (
    <g>
      {Array.from({ length: 9 }, (_, i) => (
        <line key={`v${i}`} x1={(i + 1) * 10} y1="0" x2={(i + 1) * 10} y2="75" stroke="rgba(255,255,255,0.05)" strokeWidth="0.3" />
      ))}
      {Array.from({ length: 7 }, (_, i) => (
        <line key={`h${i}`} x1="0" y1={(i + 1) * 10} x2="100" y2={(i + 1) * 10} stroke="rgba(255,255,255,0.05)" strokeWidth="0.3" />
      ))}
      <line x1="0" y1="58" x2="100" y2="34" stroke="rgba(255,255,255,0.09)" strokeWidth="0.8" />
      <line x1="30" y1="0" x2="52" y2="75" stroke="rgba(255,255,255,0.09)" strokeWidth="0.8" />
    </g>
  );
}

function HotspotMap() {
  return (
    <svg viewBox="0 0 100 75" className="w-full h-auto block" role="img" aria-label="Anonymized address hotspot map">
      <rect width="100" height="75" fill="#0d0d0d" />
      <MapGrid />
      {CLUSTERS.map((c, ci) => (
        <g key={ci}>
          <circle cx={c.cx} cy={c.cy} r={c.r + 3} fill="rgba(212,175,55,0.08)" />
          {Array.from({ length: c.n }, (_, i) => {
            const a = rand(i, ci * 7 + 1) * Math.PI * 2;
            const d = Math.sqrt(rand(i, ci * 7 + 2)) * c.r;
            return (
              <circle
                key={i}
                cx={c.cx + Math.cos(a) * d}
                cy={c.cy + Math.sin(a) * d * 0.8}
                r={0.9}
                fill="#d4af37"
                opacity={0.55 + rand(i, ci * 7 + 3) * 0.45}
              />
            );
          })}
        </g>
      ))}
    </svg>
  );
}

function ConversionMap() {
  const visits = Array.from({ length: 26 }, (_, i) => ({
    x: 50 + (rand(i, 11) - 0.5) * 34,
    y: 38 + (rand(i, 12) - 0.5) * 26,
  }));
  return (
    <svg viewBox="0 0 100 75" className="w-full h-auto block" role="img" aria-label="Anonymized conversion zone map">
      <rect width="100" height="75" fill="#0d0d0d" />
      <MapGrid />
      <polygon
        points="28,22 72,18 84,42 66,62 32,58 20,40"
        fill="rgba(212,175,55,0.06)"
        stroke="#d4af37"
        strokeWidth="0.7"
        strokeDasharray="2.4 1.6"
      />
      {visits.map((v, i) => (
        <circle key={i} cx={v.x} cy={v.y} r={0.9} fill="#d4af37" opacity={0.5 + rand(i, 13) * 0.5} />
      ))}
      <g>
        <circle cx="50" cy="38" r="4.2" fill="none" stroke="#d4af37" strokeWidth="0.5" opacity="0.6" />
        <path d="M50 33.4 L52.4 38 L50 42.6 L47.6 38 Z" fill="#d4af37" />
      </g>
    </svg>
  );
}

function MapPanel({ title, children, testId }) {
  return (
    <div className="border border-[#d4af37]/25 bg-[#0b0b0b]" data-testid={testId}>
      <div className="px-6 py-4 border-b border-[#d4af37]/15 text-[11px] tracking-[0.25em] uppercase text-[#d4af37]">{title}</div>
      {children}
    </div>
  );
}

function Legend({ items }) {
  return (
    <div className="px-6 py-4 border-t border-[#d4af37]/15 flex flex-wrap gap-x-6 gap-y-2">
      {items.map(([label, kind]) => (
        <span key={label} className="flex items-center gap-2 text-[11px] tracking-wide text-white/60">
          {kind === "dot" && <span className="w-1.5 h-1.5 rounded-full bg-[#d4af37] inline-block" />}
          {kind === "glow" && <span className="w-3 h-3 rounded-full bg-[#d4af37]/20 inline-block" />}
          {kind === "dash" && <span className="w-4 border-t border-dashed border-[#d4af37] inline-block" />}
          {kind === "marker" && <span className="w-2 h-2 rotate-45 bg-[#d4af37] inline-block" />}
          {label}
        </span>
      ))}
    </div>
  );
}
